import type { Player } from '@uefadigital/panenka';

import { loadFullPlayerData, loadPlayersFromFpz } from './api';
import type { PlayerItem, RelatedPlayersOptions } from './types';

import { fetchTeamsById } from '@apis/teams';
import { hydrateWithTranslations } from '@services/TranslatorService';

export const getPlayersWithOptions = async (
  existingPlayers: PlayerItem[],
  options: RelatedPlayersOptions,
  type?: 'club' | 'team',
): Promise<PlayerItem[]> => {
  const competitionGroups = [];

  if (type === 'club') {
    let competitionGroup: string;
    if (options.isClub) {
      competitionGroup = options.competitionGroup;
    } else {
      competitionGroup = options.gender === 'MALE' ? 'MEN_RELATED_PLAYER' : 'WOMEN_RELATED_PLAYER';
    }
    competitionGroups.push(competitionGroup);
  } else if (type === 'team') {
    competitionGroups.push(options.gender === 'MALE' ? 'MEN_RELATED_PLAYER' : 'WOMEN_RELATED_PLAYER', 'NTC');
  }

  const fetchedPlayers = [];
  for (const group of competitionGroups) {
    const callOptions = { ...options, competitionGroup: group };
    const players = await loadPlayersFromFpz(callOptions, type);
    const filteredPlayers = players.filter(
      (p) =>
        p.context.some((c) => c.competition?.id === callOptions.competitionId) &&
        !existingPlayers.some((e) => e.id === p.id) &&
        !fetchedPlayers.some((f) => f.id === p.id),
    );
    fetchedPlayers.push(...filteredPlayers);
  }

  return [...existingPlayers, ...fetchedPlayers.slice(0, options.limit)];
};

export { loadFullPlayerData as fetchFullPlayersData };

export const enrichPlayersWithAdditionalData = async (
  fullPlayers: Player[],
  initialPlayers: PlayerItem[],
  options: RelatedPlayersOptions,
) => {
  const teamIds = [...new Set(fullPlayers.flatMap((p) => [p.clubId, p.nationalTeamId]).filter(Boolean))];
  const teams = (await fetchTeamsById(teamIds)) ?? [];

  return initialPlayers.map((playerItem) => {
    const fullPlayer = hydrateWithTranslations(fullPlayers.find((p) => p.id === playerItem.id));
    const playerTeam = hydrateWithTranslations(teams.find((t) => t.id === fullPlayer.clubId));
    const playerNationalTeam = hydrateWithTranslations(teams.find((t) => t.id === fullPlayer.nationalTeamId));

    const playerContext = playerItem.context ?? [];
    const sortedContextBySeasonYear = [...playerContext].sort(
      (a, b) => Number(b.competition.seasonYear) - Number(a.competition.seasonYear),
    );
    const context = playerContext.find((c) => c.competition?.id === options.competitionId)
      ? [playerContext.find((c) => c.competition?.id === options.competitionId)]
      : sortedContextBySeasonYear;

    return {
      ...fullPlayer,
      team: playerTeam,
      nationalTeam: playerNationalTeam,
      context: context,
      imageUrl: context[0]?.images?.imageUrl ?? null,
    } as PlayerItem;
  });
};
